"use strict";
/**
 * BEST2 Control Flow Operations
 *
 * This module implements control flow opcodes for the BEST2 interpreter:
 * - Unconditional jumps (JMP)
 * - Conditional jumps based on flags (JC, JNC, JZ, JNZ, JB, JNB, JA, JNA, JL, JNL, JG, JNG)
 * - Subroutine calls (CALL/JTSR, RET)
 * - Loop instruction (LOOP)
 *
 * Jump targets are relative offsets from the instruction following the jump.
 * The offset is a signed 32-bit value added to the current program counter.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.jmp = jmp;
exports.jc = jc;
exports.jb_alias = jc;
exports.jnc = jnc;
exports.jae = jnc;
exports.jnb_alias = jnc;
exports.jz = jz;
exports.jnz = jnz;
exports.jb = jb;
exports.jnb = jnb;
exports.ja = ja;
exports.jna = jna;
exports.jbe = jna;
exports.jl = jl;
exports.jnl = jnl;
exports.jge = jnl;
exports.jg = jg;
exports.jng = jng;
exports.jle = jng;
exports.jv = jv;
exports.jo = jv;
exports.jnv = jnv;
exports.jno = jnv;
exports.jmi = jmi;
exports.js = jmi;
exports.jpl = jpl;
exports.jns = jpl;
exports.call = call;
exports.jtsr = call;
exports.ret = ret;
exports.loop = loop;
exports.jt = jt;
exports.jnt = jnt;
/**
 * Unconditional jump (JMP).
 *
 * Transfers execution to the target address unconditionally.
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC (signed 32-bit)
 * @returns New program counter
 *
 * @example
 * ```ts
 * // Jump forward 10 bytes
 * const result = jmp({ pc: 100, flags, callStack }, 10);
 * // result.newPc === 110
 * ```
 */
function jmp(state, offset) {
    return { newPc: state.pc + offset };
}
/**
 * Jump if Carry (JC) - also known as Jump if Below (JB) for unsigned comparisons.
 *
 * Jumps to target if the carry flag is set.
 * After CMP a, b: jumps if a < b (unsigned).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jc(state, offset) {
    if (state.flags.c) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Not Carry (JNC) - also known as Jump if Above or Equal (JAE) for unsigned comparisons.
 *
 * Jumps to target if the carry flag is clear.
 * After CMP a, b: jumps if a >= b (unsigned).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jnc(state, offset) {
    if (!state.flags.c) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Zero (JZ) - also known as Jump if Equal (JE).
 *
 * Jumps to target if the zero flag is set.
 * After CMP a, b: jumps if a == b.
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jz(state, offset) {
    if (state.flags.z) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Not Zero (JNZ) - also known as Jump if Not Equal (JNE).
 *
 * Jumps to target if the zero flag is clear.
 * After CMP a, b: jumps if a != b.
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jnz(state, offset) {
    if (!state.flags.z) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Below (JB) - unsigned comparison.
 *
 * Jumps to target if carry flag is set (same as JC).
 * After CMP a, b: jumps if a < b (unsigned).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jb(state, offset) {
    return jc(state, offset);
}
/**
 * Jump if Not Below (JNB) - also known as Jump if Above or Equal (JAE).
 *
 * Jumps to target if carry flag is clear (same as JNC).
 * After CMP a, b: jumps if a >= b (unsigned).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jnb(state, offset) {
    return jnc(state, offset);
}
/**
 * Jump if Above (JA) - unsigned comparison.
 *
 * Jumps to target if carry flag is clear AND zero flag is clear.
 * After CMP a, b: jumps if a > b (unsigned).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function ja(state, offset) {
    if (!state.flags.c && !state.flags.z) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Not Above (JNA) - also known as Jump if Below or Equal (JBE).
 *
 * Jumps to target if carry flag is set OR zero flag is set.
 * After CMP a, b: jumps if a <= b (unsigned).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jna(state, offset) {
    if (state.flags.c || state.flags.z) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Less (JL) - signed comparison.
 *
 * Jumps to target if sign flag != overflow flag.
 * After CMP a, b: jumps if a < b (signed).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jl(state, offset) {
    if (state.flags.s !== state.flags.v) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Not Less (JNL) - also known as Jump if Greater or Equal (JGE).
 *
 * Jumps to target if sign flag == overflow flag.
 * After CMP a, b: jumps if a >= b (signed).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jnl(state, offset) {
    if (state.flags.s === state.flags.v) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Greater (JG) - signed comparison.
 *
 * Jumps to target if zero flag is clear AND sign flag == overflow flag.
 * After CMP a, b: jumps if a > b (signed).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jg(state, offset) {
    if (!state.flags.z && state.flags.s === state.flags.v) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Not Greater (JNG) - also known as Jump if Less or Equal (JLE).
 *
 * Jumps to target if zero flag is set OR sign flag != overflow flag.
 * After CMP a, b: jumps if a <= b (signed).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jng(state, offset) {
    if (state.flags.z || state.flags.s !== state.flags.v) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Overflow (JV/JO).
 *
 * Jumps to target if overflow flag is set.
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jv(state, offset) {
    if (state.flags.v) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if No Overflow (JNV/JNO).
 *
 * Jumps to target if overflow flag is clear.
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jnv(state, offset) {
    if (!state.flags.v) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Minus (JMI/JS) - sign flag set.
 *
 * Jumps to target if sign flag is set (result was negative).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jmi(state, offset) {
    if (state.flags.s) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if Plus (JPL/JNS) - sign flag clear.
 *
 * Jumps to target if sign flag is clear (result was positive or zero).
 *
 * @param state - Current execution state
 * @param offset - Relative offset from current PC
 * @returns New program counter
 */
function jpl(state, offset) {
    if (!state.flags.s) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Call Subroutine (CALL/JTSR - Jump To SubRoutine).
 *
 * Pushes the current program counter onto the call stack and jumps to target.
 * The return address is the address of the instruction following the CALL.
 *
 * @param state - Current execution state
 * @param offset - Relative offset to subroutine
 * @returns New program counter
 * @throws {EdiabasError} If call stack overflow
 *
 * @example
 * ```ts
 * // Call subroutine at PC + 100
 * const result = call({ pc: 50, flags, callStack }, 100);
 * // result.newPc === 150
 * // callStack now contains return address 50
 * ```
 */
function call(state, offset) {
    // Push return address (current PC, which is already past the CALL instruction)
    state.callStack.push(state.pc);
    // Jump to subroutine
    return { newPc: state.pc + offset };
}
/**
 * Return from Subroutine (RET).
 *
 * Pops the return address from the call stack and jumps to it.
 *
 * @param state - Current execution state
 * @returns New program counter and continuation flag
 * @throws {EdiabasError} If call stack underflow (RET without matching CALL)
 *
 * @example
 * ```ts
 * // Return to caller
 * callStack.push(200); // Simulating prior CALL
 * const result = ret({ pc: 300, flags, callStack });
 * // result.newPc === 200
 * ```
 */
function ret(state) {
    const returnAddress = state.callStack.pop();
    return { newPc: returnAddress };
}
/**
 * Loop instruction (LOOP).
 *
 * Decrements the counter register. If the result is not zero, jumps to target.
 * This is a combined decrement-and-branch operation commonly used for counted loops.
 *
 * Note: The counter value should be managed by the caller. This function takes
 * the current counter value and returns whether to jump and the new counter value.
 *
 * @param counter - Current loop counter value
 * @param state - Current execution state
 * @param offset - Relative offset if loop continues
 * @returns Object with new counter, new PC, and flags update info
 *
 * @example
 * ```ts
 * // Loop 5 times
 * let counter = 5;
 * const result = loop(counter, { pc: 100, flags, callStack }, -20);
 * // result.newCounter === 4
 * // result.newPc === 80 (jumped back)
 * // result.shouldJump === true
 * ```
 */
function loop(counter, state, offset) {
    // Decrement counter (wrapping at 32-bit unsigned boundary)
    // Use >>> 0 to convert to unsigned 32-bit integer
    const newCounter = (counter - 1) >>> 0;
    // Jump if counter is not zero
    if (newCounter !== 0) {
        return {
            newCounter,
            newPc: state.pc + offset,
            shouldJump: true,
        };
    }
    return {
        newCounter,
        newPc: state.pc,
        shouldJump: false,
    };
}
function isTrapErrorDetected(errorTrapBitNr, testBit, hasArg, noArgUsesAnyError) {
    if (hasArg && testBit !== null) {
        if (testBit > 0) {
            if (errorTrapBitNr === testBit) {
                return true;
            }
            if (errorTrapBitNr === 0 && testBit === 32) {
                return true;
            }
        }
        else if (errorTrapBitNr >= 0x40000000) {
            return true;
        }
        return false;
    }
    if (noArgUsesAnyError) {
        return errorTrapBitNr >= 0;
    }
    return errorTrapBitNr >= 0x40000000;
}
/**
 * Jump if trap/error detected (JT).
 */
function jt(state, trapState, offset, testBit) {
    const hasArg = typeof testBit === "number";
    const detected = isTrapErrorDetected(trapState.errorTrapBitNr, hasArg ? testBit : null, hasArg, true);
    if (detected) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
/**
 * Jump if no trap/error detected (JNT).
 */
function jnt(state, trapState, offset, testBit) {
    const hasArg = typeof testBit === "number";
    const detected = isTrapErrorDetected(trapState.errorTrapBitNr, hasArg ? testBit : null, hasArg, false);
    if (!detected) {
        return { newPc: state.pc + offset };
    }
    return { newPc: state.pc };
}
//# sourceMappingURL=control-flow.js.map